import { apiClient } from '@/api/client';
import { useNotifications } from '@/lib/notifications';
import { queryKeys } from '@/lib/queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export type BackgroundJobStatus =
  | 'pending'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'paused'
  | 'cancelled';

export type BackgroundJobType = 'import' | 'score-recalculation';

export interface BackgroundJobProgress {
  id: number;
  type: BackgroundJobType;
  status: BackgroundJobStatus;
  totalItems: number;
  processedItems: number;
  successfulItems: number;
  failedItems: number;
  skippedItems: number;
  percentComplete: number;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export const useBackgroundJob = (jobId: number | null) => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_BACKGROUND_JOB, jobId],
    queryFn: async () => {
      if (!jobId) return null;
      const response = await apiClient.GET('/admin/jobs/{id}', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to fetch job progress');
      return response.data as BackgroundJobProgress;
    },
    enabled: jobId !== null,
    refetchInterval: query => {
      const status = query.state.data?.status;
      if (status === 'processing' || status === 'pending') {
        return 1000;
      }
      return false;
    },
  });
};

export const useBackgroundJobs = (type?: BackgroundJobType) => {
  return useQuery({
    queryKey: [queryKeys.ADMIN_BACKGROUND_JOB, 'list', type],
    queryFn: async () => {
      const response = await apiClient.GET('/admin/jobs', {
        params: { query: { type } },
      });
      if (response.error) throw new Error('Failed to fetch jobs');
      return response.data as BackgroundJobProgress[];
    },
  });
};

export const useBackgroundJobActions = () => {
  const { addNotification } = useNotifications();
  const queryClient = useQueryClient();

  const invalidateJobs = () => {
    queryClient.invalidateQueries({
      queryKey: [queryKeys.ADMIN_BACKGROUND_JOB],
    });
  };

  const pauseJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/jobs/{id}/pause', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to pause job');
      return response.data as BackgroundJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'info',
        title: 'Job Paused',
        message: 'Background job has been paused',
      });
      invalidateJobs();
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to pause job',
      });
    },
  });

  const cancelJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.POST('/admin/jobs/{id}/cancel', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to cancel job');
      return response.data as BackgroundJobProgress;
    },
    onSuccess: () => {
      addNotification({
        type: 'warning',
        title: 'Job Cancelled',
        message: 'Background job has been cancelled',
      });
      invalidateJobs();
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to cancel job',
      });
    },
  });

  const deleteJob = useMutation({
    mutationFn: async (jobId: number) => {
      const response = await apiClient.DELETE('/admin/jobs/{id}', {
        params: { path: { id: jobId } },
      });
      if (response.error) throw new Error('Failed to delete job');
    },
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Job Deleted',
        message: 'Background job has been deleted',
      });
      invalidateJobs();
    },
    onError: (error: Error) => {
      addNotification({
        type: 'error',
        title: 'Error',
        message: error.message || 'Failed to delete job',
      });
    },
  });

  return {
    pauseJob,
    cancelJob,
    deleteJob,
  };
};

export const isJobActive = (status: BackgroundJobStatus): boolean => {
  return status === 'pending' || status === 'processing';
};

export const isJobComplete = (status: BackgroundJobStatus): boolean => {
  return (
    status === 'completed' || status === 'failed' || status === 'cancelled'
  );
};
